// ─────────────────────────────────────────────────────────────────────────────
// BandMemberSpotlight, the hero card for the plain (non-AV) band member view.
// 1:1 port of band-member-wiring.js + #bm-spotlight-card hero, rebuilt on CD's
// React spotlight shell (.spotlight / .statile). Shows the next confirmed show +
// call times + YTD income with this band. The recent-shows strip is NOT here, it
// lives below as <RecentShows>, replacing Biggest movers for this role.
// ─────────────────────────────────────────────────────────────────────────────

import type { CSSProperties, JSX } from 'react';
import { useBandMemberSpotlight } from '../../lib';
import type { RawBookingEvent, LineupAct } from '../../lib/booking';

// Live press-photo hero treatment, identical to Spotlight.tsx (crimson gradient
// over the photo). Kept local; our photos are self-hosted base64 data URLs.
function spotlightStyle(photoUrl: string): CSSProperties | undefined {
  if (!photoUrl) return undefined;
  const safe = photoUrl.replace(/["'()\\\s]/g, '');
  return {
    backgroundImage:
      'linear-gradient(135deg,rgba(26,14,13,0.92) 0%,rgba(45,21,24,0.78) 45%,rgba(139,26,43,0.55) 100%),' +
      "url('" + safe + "')",
    backgroundSize: 'cover, cover',
    backgroundPosition: 'center, center',
    backgroundRepeat: 'no-repeat, no-repeat',
  };
}

function daysUntil(iso: string): number {
  if (!iso) return 0;
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((d.getTime() - today.getTime()) / 86400000));
}
function formatFullDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}
function formatMoney(n: number): string {
  if (n == null || isNaN(n)) return '-';
  return '$' + Math.round(Number(n)).toLocaleString('en-US');
}

// Load-in: loadInProd, else a per-band loadIn map joined, else the string. (CRIT-21)
function loadInStr(ev: RawBookingEvent | null): string {
  const dos = (ev && ev.dayOfShow) || {};
  if (dos.loadInProd) return dos.loadInProd;
  const li = dos.loadIn;
  if (li && typeof li === 'object') return Object.values(li).filter(Boolean).join(' · ') || '-';
  return (typeof li === 'string' && li) || '-';
}
// Showtime: headliner lineup slot startTime, else dos.showtime (loose legacy field).
function showtimeStr(ev: RawBookingEvent | null): string {
  const dos = (ev && ev.dayOfShow) || {};
  const lineup: ReadonlyArray<LineupAct> = Array.isArray(ev?.lineup)
    ? (ev!.lineup as ReadonlyArray<LineupAct>)
    : [];
  const headliner = lineup.find((a) => a && (a.role === 'headliner' || a.role === 'self')) || lineup[0];
  return (headliner && headliner.startTime) || (dos as { showtime?: string }).showtime || '-';
}

export interface BandMemberSpotlightProps {
  /** Live spotlight background photo (base64 data URL), or '' when none. */
  readonly photoUrl?: string;
}

export function BandMemberSpotlight({ photoUrl = '' }: BandMemberSpotlightProps): JSX.Element {
  const { data } = useBandMemberSpotlight();
  const { nextShow, ytd } = data;

  const hasPhoto = !!photoUrl;
  const cardClass = 'spotlight' + (hasPhoto ? ' spotlight--has-photo' : '');
  const cardStyle = spotlightStyle(photoUrl);

  // Hero copy.
  let eyebrow = '★ Next show';
  let title = 'No upcoming show';
  let sub = 'Confirmed shows surface here with call times and your year-to-date income with this band.';
  if (nextShow) {
    const venue = nextShow.name || nextShow.venue || 'Untitled venue';
    const city = nextShow.city || '';
    const out = daysUntil(String(nextShow.date || ''));
    const when = out === 0 ? 'TODAY' : out === 1 ? 'TOMORROW' : 'IN ' + out + ' DAYS';
    eyebrow = '★ Next show · ' + when;
    title = city ? venue + ', ' + city : venue;
    sub = formatFullDate(String(nextShow.date || ''));
  }

  return (
    <article className={cardClass} id="bm-spotlight-card" style={cardStyle}>
      {!hasPhoto && <div className="spotlight__bg" aria-hidden="true"></div>}

      <header className="spotlight__head">
        <div>
          <p className="spotlight__eyebrow">{eyebrow}</p>
          <h2 className="spotlight__title disp">{title}</h2>
          <p className="spotlight__sub">{sub}</p>
        </div>
      </header>

      <div className="spotlight__stats">
        <div className="statile">
          <p className="statile__label">Load-in</p>
          <p className="statile__value statile__value--sm">{loadInStr(nextShow)}</p>
        </div>
        <div className="statile">
          <p className="statile__label">Showtime</p>
          <p className="statile__value statile__value--sm">{showtimeStr(nextShow)}</p>
        </div>
        <div className="statile">
          <p className="statile__label">YTD with band</p>
          <p className="statile__value">{formatMoney(ytd)}</p>
        </div>
      </div>
    </article>
  );
}
